import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { buildApiUrl } from '../../config/apiConfig';
import { useStoreSettings } from '../../hooks/useStoreSettings';
import WhatsAppCoordinateButton from '../../components/WhatsAppCoordinateButton/WhatsAppCoordinateButton';
import {
  formatMoneyArs,
  isPhysicalProductType,
  orderLinesFromPurchase,
} from '../../utils/whatsapp';
import './PaymentSuccessPage.css';
import { logError } from '../../utils/logger';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { clearCart } = useCart();
  const { settings } = useStoreSettings();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [order, setOrder] = useState(null);

  const state = location.state || {};

  useEffect(() => {
    const paymentId = searchParams.get('payment_id') || state.payment_id;
    const status = searchParams.get('status') || state.status;
    const externalReference =
      searchParams.get('external_reference') || state.external_reference;

    if (state.fromBricksPayment && state.payment_id != null) {
      clearCart();
      setSuccess(true);
      setLoading(false);
      if (state.order_id && token) {
        fetch(buildApiUrl(`/api/orders/${state.order_id}`), {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data?.order) setOrder(data.order);
          })
          .catch(() => {});
      }
      return;
    }

    if (!paymentId || !status) {
      setError('Parámetros de pago inválidos');
      setLoading(false);
      return;
    }

    if (status === 'approved') {
      processPayment(paymentId, externalReference);
    } else {
      setError(`El pago no fue aprobado. Estado: ${status}`);
      setLoading(false);
    }
  }, [searchParams, location.state, token, clearCart]);

  const processPayment = async (paymentId, externalReference) => {
    try {
      const response = await fetch(buildApiUrl('/api/payments/process-approved'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          payment_id: paymentId,
          external_reference: externalReference,
        }),
      });

      if (!response.ok) {
        throw new Error('Error al procesar el pago');
      }

      const data = await response.json().catch(() => ({}));
      clearCart();
      setSuccess(true);
      if (data.order) setOrder(data.order);
    } catch (err) {
      logError('[PaymentSuccessPage] Error procesando pago:', err);
      setError(err.message || 'Error al procesar el pago');
    } finally {
      setLoading(false);
    }
  };

  const physicalLines = useMemo(() => {
    if (order?.items) {
      return orderLinesFromPurchase(order).filter((line) =>
        isPhysicalProductType(line.productType),
      );
    }
    return Array.isArray(state.physicalLines) ? state.physicalLines : [];
  }, [order, state.physicalLines]);

  const hasPhysical = physicalLines.length > 0 || Boolean(state.hasPhysical);
  const orderNumber = order?.orderNumber || state.order_number;
  const totalAmount = order?.totalAmount ?? state.total;
  const whatsappLines = physicalLines.length ? physicalLines : orderLinesFromPurchase(order);

  if (loading) {
    return (
      <div className="PS-page">
        <div className="PS-panel PS-panel--center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <h3>Procesando tu pago...</h3>
          <p>Por favor espera mientras confirmamos tu transacción.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="PS-page">
        <div className="PS-panel">
          <Alert variant="danger">
            <Alert.Heading>Error en el pago</Alert.Heading>
            <p>{error}</p>
          </Alert>
          <div className="PS-error-actions">
            <Button variant="primary" onClick={() => navigate('/cart')}>
              Volver al Carrito
            </Button>
            <Button variant="outline-secondary" onClick={() => navigate('/courses')}>
              Ir a Cursos
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="PS-page">
        <section className="PS-panel" aria-labelledby="ps-title">
          <div className="PS-grid">
            <div className="PS-confirm">
              <div className="PS-icon" aria-hidden="true">
                <i className="bi-check-lg" />
              </div>
              <p className="PS-kicker">Pago acreditado</p>
              <h1 id="ps-title" className="PS-title">
                ¡Listo, ya está confirmado!
              </h1>
              <p className="PS-lead">
                Recibirás un correo con el comprobante. Podés ver el detalle en Mis compras cuando quieras.
              </p>

              {(orderNumber || totalAmount != null) && (
                <div className="PS-meta">
                  {orderNumber ? (
                    <div className="PS-meta-item">
                      <span className="PS-meta-label">Orden</span>
                      <span className="PS-meta-value">{orderNumber}</span>
                    </div>
                  ) : null}
                  {totalAmount != null ? (
                    <div className="PS-meta-item">
                      <span className="PS-meta-label">Total</span>
                      <span className="PS-meta-value">
                        {formatMoneyArs(totalAmount, order?.currency)}
                      </span>
                    </div>
                  ) : null}
                </div>
              )}

              <div className="PS-nav">
                <Button
                  variant="outline-primary"
                  size="lg"
                  className="PS-nav-btn"
                  onClick={() => navigate('/my-purchases')}
                >
                  Ver mis compras
                </Button>
                <button type="button" className="PS-home" onClick={() => navigate('/')}>
                  Ir al inicio
                </button>
              </div>
            </div>

            <aside className="PS-wa">
              <div className="PS-wa-top">
                <span className="PS-wa-badge" aria-hidden="true">
                  <i className="fab fa-whatsapp" />
                </span>
                <div className="PS-wa-copy">
                  <p className="PS-wa-title">Envios coordinados por whatsapp*</p>
                  <p className="PS-wa-text">
                    {hasPhysical
                      ? 'Escribinos ahora para acordar la entrega de tus productos. El mensaje ya incluye tu compra.'
                      : 'Si necesitás ayuda con tu compra, escribinos al WhatsApp de Always Aligners.'}
                  </p>
                </div>
              </div>
              <WhatsAppCoordinateButton
                phone={settings.whatsappPhone}
                firstName={user?.nombre}
                orderNumber={orderNumber}
                totalAmount={totalAmount}
                currency={order?.currency}
                lines={whatsappLines}
                className="wa-coordinate-btn--lg wa-coordinate-btn--block"
              >
                Escribir por WhatsApp
              </WhatsAppCoordinateButton>
            </aside>
          </div>
        </section>
      </div>
    );
  }

  return null;
};

export default PaymentSuccessPage;
